import React, { useEffect, useState } from 'react';

import { MenuItem } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';

import { useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useUserHook from 'hooks/useUserHook';

import { useClusterRCFAnalysisStore } from 'store/clusterRCFAnalysis.store';
import { useProjectTreeStore } from 'store/projectContract.store';

import { useMutationChangeNlpThresholds } from 'api/mutations/changeNlpThresholds.mutation';
import { useQueryGetNlpThresholds } from 'api/queries/nlpThresholds.query';

import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import Icon from 'components/common/Icons/base/Icon/Icon';
import SliderComponent from 'components/common/Slider/Slider';

import classes from 'components/Dashboards/ExcellenceApps/ClusterRCFAnalysis/nlpThresholds/nlpThresholds.module.scss';
import { Button } from 'components/DesignSystem/Button';
import { Modal } from 'components/DesignSystem/Modal';
import { Select } from 'components/DesignSystem/Select';
import { CustomizedDropdownMenu, CustomizedRoundIcon, CustomizedTooltip } from 'components/common';

enum ClusterServiceType {
    REGEX = 'REGEX',
    NLP = 'NLP',
}
interface IClusterServiceTypeOptions {
    label: 'CORE_RE' | 'CORE_MD';
    value: ClusterServiceType;
}

const clusterServiceTypeOptions: IClusterServiceTypeOptions[] = [
    { value: ClusterServiceType.REGEX, label: 'CORE_RE' },
    { value: ClusterServiceType.NLP, label: 'CORE_MD' },
];

interface INlpThresholdsForm {
    clusterAbvs: number;
    clusterThreshold: number;
    minInCluster: number;
    clusterServiceType?: IClusterServiceTypeOptions | undefined;
}

export const NlpThresholds = () => {
    const { ability } = useUserHook();
    const { project } = useProjectTreeStore();
    const projectId = project?.id;

    const { update } = useClusterRCFAnalysisStore();
    const [showModal, setShowModal] = useState<boolean>(false);
    const { data, refetch } = useQueryGetNlpThresholds({ projectId });
    const { mutate, isLoading } = useMutationChangeNlpThresholds();
    const { handleSubmit, control, reset } = useForm<INlpThresholdsForm>();
    const { handleSnackBar } = useCustomSnackBar();
    const queryClient = useQueryClient();

    useEffect(() => {
        if (data) {
            reset({
                clusterAbvs: data.clusterAbvs,
                clusterThreshold: data.clusterThreshold,
                minInCluster: data.minInCluster,
                clusterServiceType: clusterServiceTypeOptions.find((item) => item.value === data.clusterServiceType),
            });
        }
    }, [data]);

    const onHideModal = () => {
        setShowModal(false);
    };

    const handleSave = (value) => {
        mutate(
            {
                projectId,
                clusterAbvs: value.clusterAbvs,
                clusterThreshold: value.clusterThreshold,
                minInCluster: value.minInCluster,
                clusterServiceType: value.clusterServiceType.value,
            },
            {
                onSuccess: () => {
                    handleSnackBar('Successfully updated!', 'success');
                },
                onError: () => {
                    handleSnackBar('Something went wrong, please try again', 'error');
                },
                onSettled: () => {
                    refetch().then(() => {
                        setShowModal(false);
                        update({ selectedClusterId: null });
                        queryClient.removeQueries('clustersList');
                    });
                },
            },
        );
    };

    const isPrivilege = ability.can('all', 'Admin') || ability.can('view', 'ManageProjects');

    if (!isPrivilege) return null;
    return (
        <>
            <CustomizedDropdownMenu
                triggerElement={
                    <CustomizedTooltip
                        tooltipPosition={TooltipPosition.Top}
                        tooltipContent={`Settings`}
                        triggerElement={
                            <CustomizedRoundIcon>
                                <Icon name={'three-dot-icon'} />
                            </CustomizedRoundIcon>
                        }
                    />
                }
            >
                <MenuItem onClick={() => setShowModal(true)}>Project NLP Settings</MenuItem>
            </CustomizedDropdownMenu>
            <Modal
                open={showModal}
                onClose={onHideModal}
                title={'Project NLP Settings'}
                actions={
                    <>
                        <Button size={'large'} variant={'secondary'} onClick={onHideModal}>
                            Cancel
                        </Button>

                        <Button
                            type={'submit'}
                            size={'large'}
                            variant={'primary'}
                            isLoading={isLoading}
                            disabled={isLoading}
                        >
                            Save
                        </Button>
                    </>
                }
            >
                <form onSubmit={handleSubmit(handleSave)}>
                    <div className={classes.container}>
                        <div className={classes.row}>
                            <label className={classes.label}>NLP Module</label>
                            <div className={classes.input}>
                                <Controller
                                    render={({ field }) => <Select {...field} options={clusterServiceTypeOptions} />}
                                    control={control}
                                    name={'clusterServiceType'}
                                />
                            </div>
                        </div>
                        <div className={classes.row}>
                            <label className={classes.label}>Match Text Threshold</label>
                            <div className={classes.input}>
                                <Controller
                                    render={({ field }) => (
                                        <SliderComponent
                                            {...field}
                                            step={0.01}
                                            min={0}
                                            max={1}
                                            valueLabelDisplay={'on'}
                                        />
                                    )}
                                    control={control}
                                    name={'clusterThreshold'}
                                />
                            </div>
                        </div>
                        <div className={classes.row}>
                            <label className={classes.label}>Abbreviations Threshold</label>
                            <div className={classes.input}>
                                <Controller
                                    render={({ field }) => (
                                        <SliderComponent
                                            {...field}
                                            step={0.1}
                                            min={0}
                                            max={1}
                                            valueLabelDisplay={'on'}
                                        />
                                    )}
                                    control={control}
                                    name={'clusterAbvs'}
                                />
                            </div>
                        </div>
                        <div className={classes.row}>
                            <label className={classes.label}>Min Cluster Size</label>
                            <div className={classes.input}>
                                <Controller
                                    render={({ field }) => (
                                        <SliderComponent
                                            {...field}
                                            step={1}
                                            min={0}
                                            max={10}
                                            valueLabelDisplay={'on'}
                                        />
                                    )}
                                    control={control}
                                    name={'minInCluster'}
                                />
                            </div>
                        </div>
                    </div>
                </form>
            </Modal>
        </>
    );
};
